import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { programText } from "@/pages/Affiliate";

export const OFFER_FLAG = "mz_affiliate_offer";

/** Shown once, right after an artist or fan account is created (Auth.jsx sets the flag):
 * presents their programme and asks whether they want to take part. Saying no is fine —
 * the programme stays reachable from the "Affiliation" page. */
export default function AffiliateOfferModal() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(OFFER_FLAG) === "1"; } catch { return false; }
  });
  if (!open || !user || !["artist", "viewer"].includes(user.role) || user.affiliate_program) return null;
  const text = programText(user.role === "artist" ? "ambassador" : "influencer");

  const done = () => {
    try { sessionStorage.removeItem(OFFER_FLAG); } catch {}
    setOpen(false);
  };
  const answer = async (yes) => {
    setBusy(true);
    try {
      if (yes) {
        const { data } = await api.post("/affiliate/join");
        await refresh();
        done();
        if (data.member) {
          toast.success("Bienvenue dans le programme ! Ton code promo t'attend.");
          navigate("/affiliation");
        } else {
          toast.success("Demande enregistrée : ton code promo sera créé automatiquement dès que tu rempliras la condition.", { duration: 7000 });
        }
      } else {
        await api.post("/affiliate/decline");
        done();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Réessaie dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[85] bg-black/85 backdrop-blur-md grid place-items-center p-4" data-testid="affiliate-offer-modal">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 space-y-4">
        <div className="w-12 h-12 rounded-2xl grid place-items-center bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] text-white"><Gift className="w-6 h-6" /></div>
        <h3 className="text-2xl font-black font-display text-white">Programme {text.title}</h3>
        <p className="text-sm text-zinc-300 leading-relaxed">{text.pitch}</p>
        <ul className="text-sm text-zinc-400 space-y-1 list-disc pl-5">
          <li>Commission de 3 % de la part nette de Metazik sur chaque achat ou vente d'un de tes affiliés, pendant 12 mois.</li>
          <li>{text.perk}</li>
          <li>{text.condition}</li>
          <li>Retrait dès 20 000 FCFA de commissions.</li>
        </ul>
        <p className="text-xs text-zinc-500">C'est gratuit et sans obligation. Tu pourras aussi rejoindre plus tard depuis la page « Affiliation ».</p>
        <div className="flex gap-2">
          <button onClick={() => answer(false)} disabled={busy} className="btn-secondary flex-1" data-testid="affiliate-offer-no">Non merci</button>
          <button onClick={() => answer(true)} disabled={busy} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="affiliate-offer-yes">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Je participe
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
